<template>
  <div class="page-content">
    <div class="head-section" v-show="showProfileView" itemscope itemtype="http://schema.org/LocalBusiness">
      <div :class="{'margin-left-18': isTablet}">
        <loader v-if="fetchingDetails"/>
        <div class="is-flex" v-else>
          <!-- Start: ITEM PROP -->
          <h1 hidden itemprop="name" v-html="bizname"></h1>
          <div hidden itemprop="image" itemscope itemtype="http://schema.org/ImageObject">
            <span itemprop="url" v-html="businessLogo"></span>
          </div>
          <div hidden itemprop="aggregateRating" itemscope itemtype="http://schema.org/AggregateRating">
            <span hidden itemprop="ratingValue" v-html="reviewAverageReal"></span>
            <span hidden itemprop="reviewCount" v-html="totalReviews"></span>
          </div>
          <!-- End: ITEM PROP -->
          <img :alt="`${business} logo`" :src="businessLogo" class="hs__biz-logo"/>
          <div :class="{'line-height-19': isMobile}">
            <img v-if="hasPaidSubscription" alt="Verified" class="hs__verified" src="/static/img/business/verified.svg"/>
            <div class="color-blue-dark has-text-weight-bold" :class="{'font-size-28': isDesktop, 'margin-bottom-4': isDesktop, 'margin-top-2': isTablet, 'margin-bottom-6': isTablet, 'font-size-24': isTablet, 'margin-bottom-2': isMobile}">
              {{ bizname }}
            </div>
            <div class="is-flex falign-center color-gray2" :class="{'font-size-10': isMobile}">
              <rating :isAvg="true" :stars="reviewAverageReal" :class="{'margin-right-15':!isMobile, 'margin-right-11':isMobile}"/>
              <span class="has-text-weight-bold">{{ reviewAverageReal }}</span>
              <span class="font-size-16">&nbsp;|&nbsp;</span>
              <span class="has-text-weight-bold">{{ totalReviews.toLocaleString("en-US") }}</span>
              &nbsp;Reviews
            </div>
          </div>
        </div>
        <div class="is-flex margin-top-10">
          <div class="hs__biz-logo" :class="{'is-hidden': !isDesktop}"/>
          <div>
            <router-link :to="{ name: 'WriteReview' }" class="hp-btn-link hp-btn-link--hollow">
              Write A Review
            </router-link>
            <a v-if="websiteFullPath" :href="websiteFullPath" class="hp-btn-link hp-btn-link--hollow" rel="nofollow" target="_blank">Visit Website</a>
          </div>
        </div>
      </div>
      <hr class="separator"/>
    </div>

    <div class="main-section">
      <div class="ms__left-box" v-show="showProfileView">
        <div v-if="roundedTrustIndex || industryRankSorted.length || averageResponseTime || getNpsTotal">
          <div :class="{'is-flex':isMobile, 'jcontent-space-between': isMobile, 'falign-center': isMobile}">
            <span class="has-text-weight-semibold color-blue-dark" :class="{'font-size-14': !isDesktop}">
              <template v-if="!isMobile">Business </template>Performance
              <span class="has-text-weight-normal">(<template v-if="!isMobile">Last </template>12 months):</span>
              &nbsp;&nbsp;&nbsp;</span>
            <a class="link--v2 font-size-12" href="#" v-if="!isDesktop" @click.prevent="openDetailView">See all</a>
          </div>
          <biz-performance v-bind="{ ...(!isMobile ? { responseTime: {value:averageResponseTime, unit: printAverageResponseTime}, nps: getNpsTotal} : {}) }" :trustIndex="roundedTrustIndex" :industries="industryRankSorted"/>
        </div>

        <div class="filter-section" ref="reviewsList" v-if="reviews.list.length">
          <div :class="{'has-text-weight-semibold': !isTablet, 'font-size-14': isMobile}" class="color-blue-dark">Filter reviews by:</div>
          <div class="filter-box hp-card">
            <div class="is-flex jcontent-space-between has-text-weight-bold" :class="{'padding-left-6': !isMobile}">
              <div>
                <img :src="`/static/img/ratings/${filterStatsStar}-star-2x.png`"/>&nbsp;<span :style="{'color': filterStatsStarColor}"> {{ reviewAverageReal }}</span>
              </div>
              <div>{{ totalReviews.toLocaleString("en-US") }} Total Reviews</div>
            </div>
            <hr class="separator"/>
            <div :class="{'padding-left-8': !isMobile, 'padding-right-14': !isMobile}">
              <div class="is-flex falign-center margin-bottom-10">
                <colored-checkbox @cb-toggle="reviewRatingCbToggleHandler($event, 5)"/>
                <rating :stars="5"/>
                <progress :class="['progress is-five']" :value="fiveStar" :max="totalReviews"/>
                <span class="fb__breakdown">{{ fiveStar.toLocaleString("en-US") }} {{ pluralize('Review', fiveStar) }}</span>
              </div>
              <div class="is-flex falign-center margin-bottom-10">
                <colored-checkbox @cb-toggle="reviewRatingCbToggleHandler($event, 4)"/>
                <rating :stars="4"/>
                <progress :class="['progress is-four']" :value="fourStar" :max="totalReviews"/>
                <span class="fb__breakdown">{{ fourStar.toLocaleString("en-US") }} {{ pluralize('Review', fourStar) }}</span>
              </div>
              <div class="is-flex falign-center margin-bottom-10">
                <colored-checkbox @cb-toggle="reviewRatingCbToggleHandler($event, 3)"/>
                <rating :stars="3"/>
                <progress :class="['progress is-three']" :value="threeStar" :max="totalReviews"/>
                <span class="fb__breakdown">{{ threeStar.toLocaleString("en-US") }} {{ pluralize('Review', threeStar) }}</span>
              </div>
              <div class="is-flex falign-center margin-bottom-10">
                <colored-checkbox @cb-toggle="reviewRatingCbToggleHandler($event, 2)"/>
                <rating :stars="2"/>
                <progress :class="['progress is-two']" :value="twoStar" :max="totalReviews"/>
                <span class="fb__breakdown">{{ twoStar.toLocaleString("en-US") }} {{ pluralize('Review', twoStar) }}</span>
              </div>
              <div class="is-flex falign-center margin-bottom-10">
                <colored-checkbox @cb-toggle="reviewRatingCbToggleHandler($event, 1)"/>
                <rating :stars="1"/>
                <progress :class="['progress is-one']" :value="oneStar" :max="totalReviews"/>
                <span class="fb__breakdown">{{ oneStar.toLocaleString("en-US") }} {{ pluralize('Review', oneStar) }}</span>
              </div>
            </div>
            <hr class="separator" v-if="isDesktop"/>
            <div class="fb__search-box">
              <input
                class="input hp-input"
                type="text"
                placeholder="Search within reviews"
                v-model="reviews.searchInput"
              />
              <button class="button hp-btn-primary hp-btn-primary--hollow" @click="reviewSearchHandler">
                <template v-if="!isMobile">SEARCH</template>
              </button>
            </div>
          </div>
        </div>

        <template v-if="!isMobileAds">
          <ad v-on:ad-loaded="updateAdCounter" :ad-object="firstAd" :no-margin="hideFirstDesktopAd"></ad>
          <p class="has-text-centered">
            <ad v-on:ad-loaded="updateAdCounter" :ad-object="secondAd"></ad>
          </p>
        </template>
        <template v-else>
          <p class="has-text-centered">
            <ad v-on:ad-loaded="updateAdCounter" :ad-object="firstAd"></ad>
          </p>
        </template>

        <!--        <div class="hp-card write-review-section is-flex jcontent-space-between falign-center" v-if="!isTablet">
                  <div><span class="color-blue-dark has-text-weight-bold" :class="{'font-size-12':isMobile}">Write a review:</span> <br v-if="isMobile"/> Let’s start by rating your experience</div>
                  <rating-new @input="takeToWriteReview"/>
                </div>-->

        <div class="review-section">
          <loader v-if="reviews.isLoading"/>
          <biz-no-result v-else-if="!reviews.list.length" :writeButton="false">
            <template #header/>
            This business has no reviews
          </biz-no-result>
          <template v-else>
            <template v-for="(review, n) in reviews.list">
              <review-card-v2 :messagesCount="review.messages.length" :replyBox="false" :messages="formatReviewMessagesForList(review.messages)" :review="formatReviewForList(review)"
                              @update:review="updateReview($event, review)" :detailLink="true" :shareLink="false"/>

              <template v-if="Math.ceil(reviews.list.length/2) === n + 1">
                <p v-if="!isMobileAds" class="has-text-centered">
                  <ad v-on:ad-loaded="updateAdCounter" :ad-object="sixthAd"></ad>
                </p>
                <p v-else class="has-text-centered">
                  <ad v-on:ad-loaded="updateAdCounter" :ad-object="secondAd"></ad>
                </p>
              </template>
            </template>
          </template>
        </div>

        <!--        <p v-if="!isMobileAds" class="has-text-centered">
                  <ad :ad-object="fourthAd" v-on:ad-loaded="updateAdCounter"></ad>
                </p>-->
        <p v-if="isMobileAds" class="has-text-centered">
          <ad v-on:ad-loaded="updateAdCounter" :ad-object="thirdAd"></ad>
        </p>

        <paginator :dataSet="reviews.pagination" @changed="changePage"/>
      </div>

      <div class="ms__right-box" v-show="showDetailView">
        <a class="link--v2 margin-bottom-27" :class="{'margin-left-14':isTablet}" href="#" v-if="!isDesktop" @click.prevent="hideDetailView">Back to company profile</a>
        <div v-if="!isDesktop" class="forder-2" :class="{'margin-bottom-14':!isDesktop}">
          <div class="color-blue-dark font-size-16 margin-left-4 margin-bottom-13" v-if="isTablet">
            <span class="has-text-weight-bold">Business Performance</span> <span class="has-text-weight-normal">(Last 12 months):</span>
          </div>
          <biz-performance :trustIndex="roundedTrustIndex" :responseTime="{value:averageResponseTime, unit: printAverageResponseTime}" :nps="getNpsTotal" :industries="industryRankSorted" :boxView="isMobile"/>
        </div>
        <p v-if="!isMobileAds" class="has-text-centered">
          <ad :ad-object="thirdAd" v-on:ad-loaded="updateAdCounter"></ad>
        </p>
        <div class="has-text-weight-bold color-blue-dark margin-bottom-24" v-if="isDesktop && (industryRankSorted.length || averageResponseTime)">See how this business has been performing on Hellopeter:</div>
        <rank-in-industries :industries="industryRankSorted" :class="{'margin-bottom-28':isDesktop, 'margin-bottom-23':isTablet, 'margin-bottom-18':isMobile, 'forder-3':!isDesktop}"/>
        <div class="response-rate-box" v-if="averageResponseTime" :class="{'hp-card': !isDesktop}">
          <div class="padding-left-3" :class="{'color-blue-dark': !isDesktop}">
            <span class="has-text-weight-bold">{{ averageResponseTime }}</span> {{ printAverageResponseTime }} <span class="has-text-weight-bold">average</span> time to reply
            <br/>
            (Last 12 months)
            <template v-if="isMobile">:</template>
          </div>
          <div class="separator"></div>
          <loader v-if="fetchingStats"/>
          <template v-else>
            <div class="padding-left-3 margin-bottom-12"><span class="has-text-weight-bold" :class="{'font-size-14':isMobile}"> {{ formatNumber(calcResponseRatePercent(responseRate.data.avgResponseCount)) }}%</span> <span
              :class="{'font-size-12':isTablet, 'font-size-11':isMobile}">Response rate</span></div>
            <div class="rrb_chart-row" v-for="response in responseRate.data.intervalBreakdown">
              <div class="is-flex falign-center" :set="percent = calcResponseRatePercent(response.count)">
                <span class="rrb_percent">{{ formatNumber(percent) }}%</span>
                <progress class="progress" :value="percent" max="100"/>
                <span class="rrb_breakdown"> {{ response.interval }}</span>
              </div>
            </div>
          </template>
        </div>
        <div :class="[aboutBizMarginBottom, {'forder-1':!isDesktop, 'margin-left-14': isTablet}]"
             v-if="hasBusinessDescription || hasBusinessAddress || hasBusinessPhone || hasBusinessEmail || hasSocialLinks">
          <div class="has-text-weight-bold margin-bottom-17 color-blue-dark" :class="{'font-size-14': isMobile}">About {{ bizname }}:</div>
          <p v-if="hasBusinessDescription" :class="{'margin-bottom-42': isDesktop, 'margin-bottom-15': !isDesktop, 'font-size-14': isMobile}">{{ record.businessDetails.description }}</p>
          <p v-if="hasBusinessAddress" class="is-flex falign-baseline margin-bottom-6">
            <img src="/static/img/others/map.svg" class="margin-right-4"/>
            &nbsp;<a target="_blank" :href="`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(record.businessDetails.address)}`" class="link--v2">{{ record.businessDetails.address }}</a>
          </p>
          <p v-if="hasBusinessPhone" class="margin-bottom-6">
            <img src="/static/img/others/phone.svg" class="valign-text-top"/>
            &nbsp;<a :href="`tel:${record.businessDetails.tel}`" class="link--v2">{{ record.businessDetails.tel }}</a>
          </p>
          <p v-if="hasBusinessEmail" class="margin-bottom-6">
            <img src="/static/img/others/email.svg" class="valign-text-top"/>
            &nbsp;<a :href="`mailto:${record.businessDetails.email}`" class="link--v2">{{ record.businessDetails.email }}</a>
          </p>
          <div v-if="hasSocialLinks">
            <a :href="socialLink.url" rel="nofollow" target="_blank" v-for="socialLink in socialLinks" class="social-link">
              <img :src="`/static/img/social/${socialLink.image}.svg`"/>
              <span></span>
            </a>
          </div>
        </div>

        <p v-if="!isMobileAds" class="has-text-centered">
          <ad :ad-object="fifthAd" v-on:ad-loaded="updateAdCounter"></ad>
        </p>

        <div :class="{'forder-5':!isDesktop}">
          <div class="has-text-weight-bold margin-bottom-12 color-blue-dark" :class="{'font-size-16': !isMobile, 'font-size-14': isMobile, 'color-blue-dark': isMobile}">About Hellopeter</div>
          <div>
          <span class="position-relative is-inline-block margin-bottom-17">
            Tips for writing helpful reviews
            <tooltip :iconRight="-26" :iconTop="2" :tooltipRight="-19" :tooltipTop="4" :openOnClick="true">
            Your reviews have a ripple effect, so make every word count. Check out our tips and FAQs about writing reviews <a href="https://blog.hellopeter.com/reviewer-insights/5-tips-write-amazing-consumer-reviews"
                                                                                                                              class="link--v2 link--no-line" target="_blank">here</a>.
            </tooltip>
          </span>
          </div>
          <div>
          <span class="position-relative is-inline-block margin-bottom-17">
            Avoid having your review removed
            <tooltip :iconRight="-26" :iconTop="2" :tooltipRight="-19" :tooltipTop="4" :openOnClick="true">
            We strive to be an impartial platform for businesses and customers to connect. Therefore, we have strict <a href="https://www.hellopeter.com/legal/legal#section6" class="link--v2 link--no-line"
                                                                                                                        target="_blank">content guidelines</a> in place to protect both parties.
            </tooltip>
          </span>
          </div>
          <div>
          <span class="position-relative is-inline-block">
            How we combat fake reviews
            <tooltip :iconRight="-26" :iconTop="2" :tooltipRight="-19" :tooltipTop="4" tooltipClass="sideTooltip" :openOnClick="true">
              We have a duty to ensure all our content is truthful. Therefore, we use a combination of AI and a team of people to do <a
              href="https://blog.hellopeter.com/reviewer-insights/can-i-really-trust-hellopeter-reviews#:~:text=Hellopeter%20has%20a%20duty%20to,of%20protection%20against%20fake%20reviews." class="link--v2 link--no-line" target="_blank">verification checks</a>. If a review breaches our <a
              href="https://www.hellopeter.com/legal/legal#section6" class="link--v2 link--no-line" target="_blank">content guidelines</a> and/or is reported, we will investigate the matter and it could get removed.
            </tooltip>
          </span>
          </div>
        </div>
        <div v-if="keywords.length" class="keywords-box">
          <div class="has-text-weight-bold color-blue-dark" :class="{'margin-bottom-17':!isMobile, 'font-size-16': !isMobile, 'font-size-14': isMobile, 'color-blue-dark': isMobile, 'margin-bottom-10':isMobile}">Popular keywords</div>
          <span v-for="keyword in keywords" class="pill" v-html="keyword"></span>
        </div>

        <p v-if="!isMobileAds" class="has-text-centered">
          <ad :ad-object="fourthAd" v-on:ad-loaded="updateAdCounter"></ad>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import qs from 'qs'
import moment from 'moment'
import {misc, moment as momentHp, adConfig} from '@/mixins'
import {
  Rating,
  Ad,
  Tooltip,
  ColoredCheckbox,
  RatingNew,
  Loader,
  BizNoResult,
  Paginator,
  ReviewCardV2,
} from '@/components'
import RankInIndustries from './RankInIndustries.vue'
import BizPerformance from './BizPerformance.vue'

export default {
  name: 'BusinessDetail',
  props: {
    business: {
      type: String,
      required: true
    }
  },
  components: {
    Rating,
    Ad,
    RankInIndustries,
    BizPerformance,
    Tooltip,
    ColoredCheckbox,
    RatingNew,
    Loader,
    BizNoResult,
    Paginator,
    ReviewCardV2
  },
  mixins: [misc, momentHp, adConfig],
  metaInfo () {
    return {
      title: this.metaTitle,
      titleTemplate: '%s | Hellopeter.com',
      meta: this.metaDescription,
      link: this.getMetaLinks,
      bodyAttrs: {
        class: 'business'
      }
    }
  },
  data () {
    return {
      hasStats: false,
      hasDetail: false,
      averageResponseTime: 0,
      fetchingStats: false,
      fetchingDetails: false,
      record: undefined,
      stats: undefined,
      oneStar: 0,
      twoStar: 0,
      threeStar: 0,
      fourStar: 0,
      fiveStar: 0,
      socialLinks: [],
      keywords: [],
      industryRankSorted: [],
      mainIndustry: {},
      reviews: {
        isLoading: false,
        searchInput: '',
        filters: {
          rating: [],
          search: '',
          page: 1
        },
        list: [],
        pagination: {},
        reviewBodyCharsShow: 430,
        replyCharsShow: 430,
      },
      responseRate: {
        data: {}
      },
      showDetailViewFlag: false
    }
  },
  computed: {
    aboutBizMarginBottom () {
      if (this.isTablet) {
        if (this.hasSocialLinks) {
          return 'margin-bottom-24'
        }

        if (this.hasBusinessAddress || this.hasBusinessPhone || this.hasBusinessEmail) {
          return 'margin-bottom-18'
        }

        if (this.hasBusinessDescription) {
          return 'margin-bottom-9'
        }
      }

      if (this.isMobile) {
        if (this.hasSocialLinks) {
          return 'margin-bottom-16'
        }

        if (this.hasBusinessAddress || this.hasBusinessPhone || this.hasBusinessEmail) {
          return 'margin-bottom-10'
        }

        if (this.hasBusinessDescription) {
          return 'margin-bottom-1'
        }
      }
    },
    roundedTrustIndex () {
      if (typeof this.record !== 'undefined' && 'elastic' in this.record && 'trustIndex' in this.record.elastic) {
        return Math.round(this.record.elastic.trustIndex * 1e1) / 1e1
      }
      return 0
    },
    bizname () {
      if (typeof this.record !== 'undefined' && typeof this.record.businessName !== 'undefined') {
        return this.record.businessName
      }
      return ""
    },
    totalReviews () {
      if (typeof this.stats !== 'undefined') {
        return parseInt(this.stats.totalReviews)
      }
      return 0
    },
    showDetailView () {
      return this.isDesktop || this.showDetailViewFlag
    },
    showProfileView () {
      return this.isDesktop || !this.showDetailViewFlag
    },
    getMetaLinks () {
      let links = []
      if (typeof this.record !== 'undefined' && typeof this.record.businessDetails !== 'undefined' && (this.reviews.pagination.current_page > 0 || this.reviews.pagination.last_page > 0)) {
        links.push({
          rel: 'canonical',
          href: `${window.location.protocol}//${window.location.host}/${this.record.businessDetails.slug}`
        })
        if (this.reviews.pagination.current_page === 1) {
          if (this.reviews.pagination.last_page > 0) {
            links.push({
              rel: 'next',
              href: `${window.location.protocol}//${window.location.host}${this.$route.path}?page=${(this.reviews.pagination.current_page + 1)}`
            })
          }
          links.push({
            rel: 'prev',
            href: `${window.location.protocol}//${window.location.host}/${this.record.businessDetails.slug}`
          })
        } else {
          if (this.reviews.pagination.current_page !== this.reviews.pagination.last_page) {
            links.push({
              rel: 'next',
              href: `${window.location.protocol}//${window.location.host}${this.$route.path}?page=${(this.reviews.pagination.current_page + 1)}`
            })
          } else {
            links.push({
              rel: 'next',
              href: `${window.location.protocol}//${window.location.host}${this.$route.path}?page=${(this.reviews.pagination.current_page)}`
            })
          }
          links.push({
            rel: 'prev',
            href: `${window.location.protocol}//${window.location.host}${this.$route.path}?page=${(this.reviews.pagination.current_page - 1)}`
          })
        }
      }
      return links
    },
    metaTitle () {
      if (typeof this.record !== 'undefined' && this.bizname) {
        return `${this.bizname} Reviews | Contact ${this.bizname} - ${Object.keys(this.mainIndustry).length ? this.mainIndustry.name + " - " : ''}${this.roundedTrustIndex} TrustIndex`
      }
      return ''
    },
    metaDescription () {
      let metaObjects = []
      if (typeof this.record !== 'undefined' && this.bizname && typeof this.record.businessDetails !== 'undefined') {
        let description = `${this.bizname} reviews, customer feedback & support. Contact & review ${this.bizname}`

        if (Object.keys(this.mainIndustry).length && this.mainIndustry.industryRanking < 50) {
          description += ` - rated #${this.mainIndustry.industryRanking} in ${this.mainIndustry.name}`
        }

        const avgResponseTime = parseInt(this.averageResponseTime)
        if (avgResponseTime < 24 && this.record.businessDetails.responding) {
          description += ` - get a reply from ${this.bizname} within an avg of ${this.averageResponseTime} ${this.pluralize('hour', avgResponseTime)}`
        }

        metaObjects.push({name: 'description', content: description})

        if (this.$route.query.page) {
          metaObjects.push({name: 'ROBOTS', content: 'NOINDEX, FOLLOW'})
        }
      }
      return metaObjects
    },
    reviewFilters () {
      return {
        ...(this.reviews.filters.rating.length && {ratings: this.reviews.filters.rating.join(',')}),
        ...(this.reviews.filters.search && {keyword: this.reviews.filters.search}),
        page: this.reviews.filters.page
      }
    },
    filterStatsStar () {
      const stars = Math.round(parseFloat(this.stats.reviewAverage) * 10) / 10
      let starRating = stars.toString()
      starRating = starRating.split('.')
      let star = starRating[0]
      let decimal = 0
      if (starRating.length > 1) {
        decimal = starRating[1].substring(0, 1)
      }
      if (decimal >= 8) {
        ++star
      }
      return star
    },
    filterStatsStarColor () {
      let color = '#58595b'
      switch (this.filterStatsStar) {
        case 5:
          color = '#00bf87'
          break;
        case 4:
          color = '#30c8a7'
          break;
        case 3:
          color = '#fbc64e'
          break;
        case 2:
          color = '#f99c50'
          break;
        case 1:
          color = '#ea3939'
          break;
      }
      return color
    },
    reviewAverageReal () {
      if (typeof this.stats !== 'undefined' && typeof this.stats.reviewAverage !== 'undefined' && this.stats.reviewAverage !== null) {
        return Math.round(parseFloat(this.stats.reviewAverage) * 1e2) / 1e2
      }
      return 0
    },
    businessLogo () {
      if (typeof this.record !== 'undefined') {
        if (this.record.businessDetails.logo !== null && this.record.businessDetails.logo !== '') {
          return this.record.businessDetails.logo
        }
      }
      return this.noLogo.url
    },
    hasBusinessDescription () {
      return typeof this.record !== 'undefined' &&
        typeof this.record.businessDetails !== 'undefined' &&
        typeof this.record.businessDetails.description === 'string' &&
        this.record.businessDetails.description.length
    },
    hasBusinessAddress () {
      return typeof this.record !== 'undefined' &&
        typeof this.record.businessDetails !== 'undefined' &&
        typeof this.record.businessDetails.address === 'string' &&
        this.record.businessDetails.address.length > 6
    },
    hasBusinessEmail () {
      return this.hasPaidSubscription &&
        typeof this.record !== 'undefined' &&
        typeof this.record.businessDetails !== 'undefined' &&
        typeof this.record.businessDetails.email === 'string' &&
        this.record.businessDetails.email.length
    },
    hasSocialLinks () {
      return this.hasPaidSubscription && this.socialLinks.length
    },
    hasBusinessPhone () {
      return this.hasPaidSubscription &&
        typeof this.record !== 'undefined' &&
        typeof this.record.businessDetails !== 'undefined' &&
        typeof this.record.businessDetails.tel === 'string' &&
        this.record.businessDetails.tel.length
    },
    getNpsTotal () {
      if (typeof this.record !== 'undefined' && typeof this.record.nps !== 'undefined') {
        return this.record.nps.nps_total
      }
      return 0
    },
    websiteFullPath () {
      if (typeof this.record !== 'undefined' && typeof this.record.businessDetails !== 'undefined' && typeof this.record.businessDetails.website !== 'undefined' && this.record.businessDetails.website !== null && this.record.businessDetails.website.length > 0) {
        const url = this.record.businessDetails.website
        if (url.startsWith('http')) {
          return this.record.businessDetails.website
        }
        return `http://${this.record.businessDetails.website}`
      }
      return ''
    },
    hasStatsAndDetailAndGraph () {
      const hasStats = parseInt(this.hasStats)
      const hasDetail = parseInt(this.hasDetail)
      return `${hasStats},${hasDetail}`
    },
    hasPaidSubscription () {
      return typeof this.record !== 'undefined' && this.record.businessDetails.isPaid
    },
    /***
     * Convert response time to hour
     * @return {void}
     */
    printAverageResponseTime () {
      if (this.averageResponseTime > 0) {
        return `Hrs`
      }
      return ``
    },
  },
  created () {
    this.getBusinessDetail()
    this.getBusinessStats()
    this.fetchReviews(false)
  },
  mounted () {
    this.loadAds('BusinessListing')
  },
  methods: {
    digitToWords (num) {
      let a = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty'];
      if ((num = num.toString()).length > 9) return 'overflow';
      let n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
      if (!n) return;
      return a[Number(n[5])]
    },
    hideDetailView () {
      this.showDetailViewFlag = false
    },
    openDetailView () {
      this.showDetailViewFlag = true
    },
    formatNumber (value) {
      const trimmedDigits = !Number.isInteger(value) ? `${Number(value).toFixed(2)}` : value
      return Math.round(trimmedDigits)
    },
    calcResponseRatePercent (value) {
      if ('totalReviews' in this.responseRate.data) {
        let rate = (value * 100) / this.responseRate.data.totalReviews
        return !isNaN(rate) ? rate : 0
      }
      return 0
    },
    updateReview (data, review) {
      review.business_reporting = data.business_reporting
    },
    changePage (newPage) {
      this.reviews.filters.page = newPage
    },
    formatReviewForList (review) {
      const clone = structuredClone(review); //Cloning otherwise it will overwrite original reviews.list
      clone.review_content = this.trimText(review.review_content, this.reviews.reviewBodyCharsShow)
      return clone
    },
    formatReviewMessagesForList (messages) {
      const message = this.bizFirstPublicmessage(messages)

      const clone = structuredClone(message) //Cloning otherwise it will overwrite original reviews.list
      if (Object.keys(clone).length) {
        clone.body = this.trimText(clone.body, this.reviews.replyCharsShow)
        return [clone]
      }
      return []
    },
    bizFirstPublicmessage (messages) {
      if (messages.length) {
        return messages.find(message => message.owner === 2 && message.type === "Public") || {}
      }
      return {}
    },
    async fetchReviews (scrollToList = true) {
      this.reviews.isLoading = true
      axios.get(`/consumer/business/${this.business}/reviews`, {
        params: this.reviewFilters
      }).then(({data}) => {
        this.reviews.list = data.data
        this.reviews.pagination = {current_page: data.current_page, prev_page_url: data.prev_page_url, next_page_url: data.next_page_url, last_page: data.last_page, per_page: data.per_page}
        this.reviews.isLoading = false
        if (scrollToList) {
          this.$nextTick(() => {
            this.$refs.reviewsList.scrollIntoView({behavior: "instant"})
          })
        }
      })
    },
    reviewRatingCbToggleHandler (isChecked, rating) {
      if (isChecked) {
        this.reviews.filters.rating.push(rating)
      } else {
        const index = this.reviews.filters.rating.indexOf(rating)
        if (index !== -1) {
          this.reviews.filters.rating.splice(index, 1)
        }
      }
    },
    reviewSearchHandler () {
      this.reviews.filters.search = this.reviews.searchInput
    },
    takeToWriteReview (rating) {
      this.$router.push({name: 'WriteReview', params: {slug: this.business}, query: {rate: rating}})
    },
    async getBusinessDetail () {
      this.fetchingDetails = true

      axios.get(`/consumer/business/${this.business}`)
        .then(({data}) => {
          this.record = data

          const arr = []
          for (const indusSlug in this.record.elastic.industries) {
            const isMainIndus = this.record.elastic.industries[indusSlug].isMain,
              obj = {
                industryRanking: this.record.elastic.industries[indusSlug].rank,
                name: this.record.elastic.industries[indusSlug].name,
                slug: indusSlug,
                isMainIndus
              };

            if (isMainIndus) {
              this.mainIndustry = obj
            }

            arr.push(obj)
          }
          this.industryRankSorted = arr

          this.hasDetail = true
          if (typeof data.businessDetails !== 'undefined') {
            this.keywords = data.businessDetails.keywords
          }

          this.addSocialLinks()
          this.fetchingDetails = false
        })
    },
    async getBusinessStats () {
      this.fetchingStats = true
      axios.get(`/consumer/business-stats/${this.business}`, {
        params: {
          responseRate: {
            ...this.calcLast12MonthsDate(),
            source: ['WEBSITE', 'LANDING_PAGE_EMAIL_INVITE', 'IN_MAIL_EMAIL_INVITE', 'PORTAL_COPY_LINK_INVITE', 'SMS_INVITE', 'QR_CODE', 'FACEBOOK_TAB', 'REVIEW_TAB_WIDGET_INVITE', 'WHATSAPP', 'WHATSAPP_INVITE'],
            status: 'ACTIVE',
            channel: 'HELLOPETER'
          }
        }
      })
        .then(({data}) => {
          this.hasStats = true
          this.stats = data
          this.averageResponseTime = data.avgResponseTime
          this.responseRate.data = data.responseRate

          if (typeof data.reviewRatings !== 'undefined' && typeof data.reviewRatings.rows !== 'undefined') {
            data.reviewRatings.rows.forEach((row) => {
              switch (row[0]) {
                case '1 Star':
                  this.oneStar = row[1]
                  break
                case '2 Stars':
                  this.twoStar = row[1]
                  break
                case '3 Stars':
                  this.threeStar = row[1]
                  break
                case '4 Stars':
                  this.fourStar = row[1]
                  break
                case '5 Stars':
                  this.fiveStar = row[1]
                  break
                default:
                  break
              }
            })
          }
        })
        .catch((error) => {
          console.log('error', error)
        })
        .finally(_ => {
          this.fetchingStats = false
        })
    },
    addSocialLinks () {
      if (typeof this.record !== 'undefined') {
        // facebook
        if (typeof this.record.businessDetails.facebook !== 'object' && typeof this.record.businessDetails.facebook !== 'undefined' && this.record.businessDetails.facebook.length > 0) {
          let url = this.record.businessDetails.facebook
          if (!url.startsWith('http')) {
            url = `https://${url}`
          }
          this.socialLinks.push({
            image: 'fb',
            url
          })
        }
        // instagram
        if (typeof this.record.businessDetails.instagram !== 'object' && typeof this.record.businessDetails.instagram !== 'undefined' && this.record.businessDetails.instagram.length > 0) {
          let url = this.record.businessDetails.instagram
          if (!url.startsWith('http')) {
            url = `https://${url}`
          }
          this.socialLinks.push({
            image: 'insta',
            url
          })
        }
        // twitter
        if (typeof this.record.businessDetails.twitter !== 'object' && typeof this.record.businessDetails.twitter !== 'undefined' && this.record.businessDetails.twitter.length > 0) {
          let url = this.record.businessDetails.twitter
          if (!url.startsWith('http')) {
            url = `https://${url}`
          }
          this.socialLinks.push({
            image: 'twitter',
            url
          })
        }
        // linkedin
        if (typeof this.record.businessDetails.linkedin !== 'object' && typeof this.record.businessDetails.linkedin !== 'undefined' && this.record.businessDetails.linkedin.length > 0) {
          let url = this.record.businessDetails.linkedin
          if (!url.startsWith('http')) {
            url = `https://${url}`
          }
          this.socialLinks.push({
            image: 'linkedin',
            url
          })
        }
        // google
        /* if (typeof this.record.businessDetails.google !== 'object' && typeof this.record.businessDetails.google !== 'undefined' && this.record.businessDetails.google.length > 0) {
          let url = this.record.businessDetails.google
          if (!url.startsWith('http')) {
            url = `https://${url}`
          }
          this.socialLinks.push({
            style: 'social-google',
            icon: 'fab fa-google',
            url: url
          })

        } */

        let trackEvent = {
          event: 'pageLoad',
          virtualPageUrl: this.$router.currentRoute.path,
          virtualPageTitle: this.metaTitle,
          brandName: this.bizname,
          industryName: this.record.industryName
        }
        if (this.record.businessCategories.length > 0) {
          let that = this
          this.record.businessCategories.forEach(function (v, i) {
            i++
            if (i >= 16) return false
            let key = 'category' + that.digitToWords(i).charAt(0).toUpperCase() + that.digitToWords(i).slice(1)
            trackEvent[key] = v.category.name
          })
        }
        if (!window.pageTracked) {
          window.pageTracked = true
          this.$gtm.trackEvent(trackEvent)
        }
      }
    }
  },
  watch: {
    hasStatsAndDetailAndGraph () {
      window.prerenderReady = true
    },
    reviewFilters: {
      handler () {
        this.fetchReviews()
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";
@import "@/assets/scss/vendor/_breakpoints.scss";

.page-content {
  max-width: 1335px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 27px;
  margin-top: 170px;
  padding: 0 20px 0 83px;
  color: map-get($colors, 'gray');

  @include tablet {
    padding: 0 40px;
    margin-top: 115px;
  }

  @include mobile {
    padding: 0 20px;
    margin-top: 94px;
  }

  .separator {
    background-color: rgba(138, 140, 145, 0.32);
  }
}

.head-section {
  font-size: 14px;
  padding-right: 56px;

  @include not-desktop {
    padding-right: 0;
  }

  @include mobile {
    .hs__verified {
      width: 116px;
    }

    .star-new {
      width: 72px;

      > > > img {
        vertical-align: text-top;
      }
    }
  }

  .hs__biz-logo {
    width: 111px;
    margin-right: 33px;
    align-self: center;
    border-radius: 50%;

    @include tablet {
      margin-left: 2px;
    }

    @include mobile {
      width: 50px;
      margin-right: 12px;
      margin-left: 6px;
    }
  }

  .hp-btn-link {
    padding: 6px 0;
    margin-right: 15px;

    @include tablet {
      width: 140px;
      font-size: 12px;
      padding: 8px 0;
      margin-top: 9px;
      margin-right: 27px;
    }

    @include mobile {
      width: 95px;
      font-size: 11px;
      padding: 2px 0;
      margin-right: 4px;
      margin-top: 10px;
      text-transform: capitalize;
    }
  }

  .separator {
    margin-top: 33px;
    margin-bottom: 29px;

    @include tablet {
      margin-top: 20px;
      margin-bottom: 20px;
    }

    @include mobile {
      margin-top: 17px;
      margin-bottom: 8px;
    }
  }
}

.main-section {
  padding-left: 7px;
  display: flex;

  @include tablet {
    padding-left: 1px;
  }

  @include mobile {
    padding-left: 0;
  }

  @mixin barColor($color) {
    &::-webkit-progress-value {
      background-color: $color;
    }

    &::-moz-progress-bar {
      background-color: $color;
    }

    &::-ms-fill {
      background-color: $color;
    }
  }

  .progress {
    border-radius: 0;
    height: 15px;
    margin: 0;
  }

  .ms__left-box {
    width: 100%;

    .filter-section {
      margin-top: 11px;

      @include tablet {
        font-size: 14px;
        max-width: 636px;
        margin: 0 auto;
      }

      .filter-box {
        margin-top: 9px;
        padding: 12px 35px 27px 23px;

        @include mobile {
          padding: 11px 16px 21px 14px;
        }

        img {
          width: 17px;
          height: 16px;
          vertical-align: text-top;
        }

        .separator:first-of-type {
          margin-top: 15px;
          margin-bottom: 6px;

          @include mobile {
            margin-top: 11px;
            margin-bottom: 8px;
          }
        }

        > > > .star-new {
          width: 75px;
          margin-left: 19px;
          margin-right: 10px;

          @include mobile {
            margin-left: 10px;
            margin-right: 6px;
          }

          li {
            font-size: 10px;
          }
        }

        .progress {
          flex-grow: 1;
          width: auto;

          /* FIVE STAR PROGRESS BAR*/
          &.is-five {
            @include barColor(#00bf87);
          }

          /* FOUR STAR PROGRESS BAR*/
          &.is-four {
            @include barColor(#30c8a7);
          }

          /* THREE STAR PROGRESS BAR*/
          &.is-three {
            @include barColor(#fbc64e);
          }

          /* TWO STAR PROGRESS BAR*/
          &.is-two {
            @include barColor(#f99c50);
          }

          /* ONE STAR PROGRESS BAR*/
          &.is-one {
            @include barColor(#ea3939);
          }
        }

        .fb__breakdown {
          font-size: 12px;
          flex-basis: 89px;
          text-align: right;

          @include mobile {
            font-size: 11px;
            flex-basis: 69px;
          }
        }

        .fb__search-box {
          position: relative;
          margin-left: 5px;

          @include tablet {
            margin-top: 21px;
          }

          @include mobile {
            margin-left: 0;
          }

          input {
            padding-right: 140px;

            @include mobile {
              font-size: 12px;
              padding-left: 12px;
              padding-right: 55px;
            }
          }

          button {
            position: absolute;
            top: 0;
            right: 0;
            border-radius: 20px;
            width: auto;
            padding-right: 35px;
            padding-left: 41px;
            font-size: 14px;
            font-weight: 600;
            background: url("/static/img/magnifier/magnifier2_blue_dark.png") no-repeat left 20px top 15px/10px;

            @include mobile {
              border-radius: 20px;
              background: url("/static/img/magnifier/magnifier2_white.png") map-get($colors, 'blue-dark') no-repeat center center/21px;
              height: 40px;
              width: 50px;
              padding: 0;
              background-size: 19px auto;
            }
          }
        }
      }
    }

    .write-review-section {
      padding: 20px 33px 21px 30px;

      @include mobile {
        flex-wrap: wrap;
        font-size: 11px;
        padding: 6px 33px 0px 15px;

        > > > .rating-input {
          margin-top: -8px;

          .rating-input__galaxy {
            padding-left: 0;
          }

          img {
            height: 14px;
          }
        }
      }
    }

    .review-section {
      margin-top: 24px;

      @include tablet {
        max-width: 656px;
        margin: 27px auto 0;
      }
    }
  }

  .ms__right-box {
    margin-left: 20px;
    flex-shrink: 0;
    width: 413px;
    padding: 0 57px 58px 56px;
    background-color: #f6f6f6;
    border-radius: 2px;
    font-size: 14px;
    display: flex;
    flex-direction: column;

    @include not-desktop {
      width: 100%;
      background-color: initial;
      margin-left: 0;
    }

    @include tablet {
      padding: 0 0 0 4px;

      > > > .perf-card {
        font-size: 10px;
        margin-right: 9px;

        .perf-card__head {
          font-size: 17px;
        }
      }
    }

    @include mobile {
      font-size: 12px;
      padding: 0;
    }

    .response-rate-box {
      border-radius: 10px;
      border: 1px solid map-get($colors, 'gray-light');
      background-color: #fff;
      margin-bottom: 28px;
      padding: 13px 13px 15px 14px;

      @include not-desktop {
        order: 4;
      }

      @include tablet {
        font-size: 16px;
        padding: 11px 12px 9px 8px;
        margin-bottom: 36px;
      }

      @include mobile {
        font-size: 12px;
        padding: 16px 13px 11px 15px;
        margin-bottom: 28px;
      }

      .separator {
        margin-top: 8px;
        margin-bottom: 11px;

        @include mobile {
          margin-bottom: 9px;
        }
      }

      .rrb_chart-row {
        font-size: 13px;
        text-align: right;
        margin-bottom: 4px;

        @include tablet {
          font-size: 14px;
          padding-left: 3px;
        }

        @include mobile {
          font-size: 11px;
          margin-bottom: 8px;
        }

        .rrb_percent {
          width: 34px;
          margin-right: 28px;

          @include tablet {
            text-align: left;
            width: 44px;
            margin-right: 0;
          }

          @include mobile {
            width: 26px;
            margin-right: 6px;
          }
        }

        .progress {
          width: 110px;
          @include barColor(map-get($colors, 'blue-dark'));

          @include tablet {
            width: 510px;
          }

          @include mobile {
            height: 7px;
            width: 137px;
          }
        }

        .rrb_breakdown {
          flex-grow: 1;
        }
      }
    }

    .social-link {
      margin-right: 18px;
      margin-top: 11px;

      @include not-desktop {
        box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16);
        border-radius: 50%;
        margin-top: 8px;
      }

      @include mobile {
        width: 30px;
        height: 30px;
      }
    }

    .keywords-box {
      margin-top: 26px;

      @include not-desktop {
        order: 6;
      }

      @include mobile {
        margin-top: 32px;
      }

      .pill {
        @include tablet {
          font-size: 11px;
        }

        @include mobile {
          margin-right: 10px;
        }
      }
    }
  }
}
</style>
<style lang="scss">
@import "@/assets/scss/_variables.scss";
@import "@/assets/scss/vendor/_breakpoints.scss";

.sideTooltip.tooltip.popover {
  width: 324px;
  font-size: 14px;

  .popover-inner {
    padding-left: 17px;
    padding-right: 17px;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/business/BusinessDetail.vue